import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import NewTag from '@/models/new_tag'
import { InventoryPickerGroup } from '../tickets/StopsAndInventories/InventoryPickers'

function createArrayState<T>(initialValue: T[] = []) {
  // https://github.com/vuejs/core/issues/2136#issuecomment-693524663
  // When using ref or reactive with generics you need to cast to as Ref<T> and as reactive<T> if you sure that the type doesn't have any nested refs, because ref and reactive will unwrap nested refs automatically.
  const state = ref(initialValue) as Ref<T[]>

  const setState = (newValue: T[]) => {
    state.value = newValue
  }

  const clearState = () => {
    state.value = []
  }

  return {
    state,
    setState,
    clearState,
  }
}

function createPrimitiveState<T>(initialValueArg: T) {
  const initialValueRef = ref(initialValueArg) as Ref<T>
  const state = ref(initialValueArg) as Ref<T>

  const setState = (newValue: T) => {
    state.value = newValue
  }

  const resetState = () => {
    state.value = initialValueRef.value
  }

  const setInitialValue = (newInitialValue: T) => {
    initialValueRef.value = newInitialValue
  }

  const resetInitialValue = () => {
    initialValueRef.value = initialValueArg
  }

  return {
    state,
    setState,
    resetState,
    initialValue: initialValueRef,
    setInitialValue,
    resetInitialValue,
  }
}

export const useJobFormDataStore = defineStore('jobFormDataStore', () => {
  const {
    state: draftTags,
    setState: setDraftTags,
    clearState: clearDraftTags,
  } = createArrayState<NewTag>()

  const {
    state: requestedStops,
    setState: setRequestedStops,
    clearState: clearRequestedStops,
  } = createArrayState<InventoryPickerGroup>()

  const {
    state: repeatJobsCount,
    setState: setRepeatJobsCount,
    resetState: resetRepeatJobsCount,
    initialValue: repeatJobsCountInitialValue,
    setInitialValue: setRepeatJobsCountInitialValue,
    resetInitialValue: resetRepeatJobsCountInitialValue,
  } = createPrimitiveState(1)

  const {
    state: isRepeatTrip,
    setState: setIsRepeatTrip,
    resetState: resetIsRepeatTrip,
    initialValue: isRepeatTripInitialValue,
    setInitialValue: setIsRepeatTripInitialValue,
    resetInitialValue: resetIsRepeatTripInitialValue,
  } = createPrimitiveState(false)

  const clearAll = () => {
    // Clear initial values
    resetRepeatJobsCountInitialValue()
    resetIsRepeatTripInitialValue()

    // Clear current values
    clearDraftTags()
    clearRequestedStops()
    resetRepeatJobsCount()
    resetIsRepeatTrip()
  }

  return {
    // Tags
    draftTags,
    setDraftTags,
    clearDraftTags,
    // Requested Stops
    requestedStops,
    setRequestedStops,
    clearRequestedStops,
    // RepeatJobsCount
    repeatJobsCount,
    setRepeatJobsCount,
    resetRepeatJobsCount,
    repeatJobsCountInitialValue,
    setRepeatJobsCountInitialValue,
    resetRepeatJobsCountInitialValue,
    // IsRepeatTrip
    isRepeatTrip,
    setIsRepeatTrip,
    resetIsRepeatTrip,
    isRepeatTripInitialValue,
    setIsRepeatTripInitialValue,
    resetIsRepeatTripInitialValue,
    // Clear all states
    clearAll,
  }
})
